import React, { useMemo } from "react";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import { DLS_TEXT_VARIANTS } from "../../../constants/dls";
import { LeftGradientLine, ReferEarnContainer, ReferEarnText, RightGradientLine } from "../../../styles/home";
import {
    ContactInputContainer,
    StyledFullWidthButton,
    LoaderContainer,
} from "../../../styles/referralView";
import { ICity } from "../../../interfaces/home";
import ReferralSuccessModal from "./ReferralSuccessModal";
import ReferralFailureModal from "./ReferralFailureModal";
import { useReferralForm } from "./useReferralForm";
import { ReactSelect, SelectOption } from "../../../components/Select";
import { Button } from "@swiggy-private/dls-web-core/dist/components/Button";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";

interface ReferralViewFormProps {
    cities: ICity[];
}

const ReferralViewForm = ({ cities }: ReferralViewFormProps) => {
    const {
        contactNumber,
        contactName,
        selectedCity,
        isLoading,
        isButtonDisabled,
        setContactNumber,
        setContactName,
        handleCityChange,
        showSuccessModal,
        showFailureModal,
        errorMessage,
        shouldRetryOnError,
        handleReferClick,
        handleCloseSuccessModal,
        handleCloseFailureModal,
        getContactNumberError,
        getContactNameError,
        getCityError,
    } = useReferralForm();

    const getReferredPersonDetails = () => {
        if (contactNumber && contactName && selectedCity) {
            const selectedCityName = cities.find((c) => c.city_id.toString() === selectedCity)?.city_name || selectedCity;
            return {
                name: contactName,
                city_id: selectedCity,
                city_name: selectedCityName,
            };
        }
        return undefined;
    };

    // Convert cities to ReactSelect options
    const cityOptions: SelectOption[] = useMemo(
        () =>
            cities?.map((city) => ({
                value: city.city_id.toString(),
                label: city.city_name,
            })) || [],
        [cities]
    );

    // Find the selected option
    const selectedCityOption = useMemo(
        () => cityOptions.find((option) => option.value === selectedCity) || null,
        [cityOptions, selectedCity]
    );

    // Handle ReactSelect onChange
    const handleReactSelectChange = (option: SelectOption | null) => {
        if (option) {
            // Create a synthetic event to match the expected signature
            const syntheticEvent = {
                target: { value: option.value },
            } as React.ChangeEvent<HTMLSelectElement>;
            handleCityChange(syntheticEvent);
        } else {
            // Handle clear case
            const syntheticEvent = {
                target: { value: "" },
            } as React.ChangeEvent<HTMLSelectElement>;
            handleCityChange(syntheticEvent);
        }
    };

    return (
        <>
            <ReferEarnContainer>
                <LeftGradientLine />
                <ReferEarnText variant={DLS_TEXT_VARIANTS.OVERLINE_NEUTRAL_BOLD_SPACED}>REFER & EARN</ReferEarnText>
                <RightGradientLine />
            </ReferEarnContainer>
            <ContactInputContainer>
                <TextField
                    textFieldDetails={{
                        value: contactNumber,
                        label: "Enter Contact Number",
                        name: "contactNumber",
                        type: TextFieldTypes.PHONE,
                        onChange: setContactNumber,
                        inputLimit: 10,
                        errorText: getContactNumberError(),
                    }}
                />
                <TextField
                    textFieldDetails={{
                        value: contactName,
                        label: "Contact Name",
                        name: "contactName",
                        type: TextFieldTypes.TEXT,
                        onChange: setContactName,
                        inputLimit: 50, 
                        validFormat: "^[a-zA-Z\\s]{0,50}$",
                        errorText: getContactNameError(),
                    }}
                />

                <ReactSelect
                    value={selectedCityOption}
                    onChange={handleReactSelectChange}
                    options={cityOptions}
                    placeholder="Select City"
                    label="Select City"
                    isClearable={false}
                    isSearchable={true}
                    error={!!getCityError()}
                    errorMessage={getCityError()}
                />

                {isLoading ? (
                    <LoaderContainer>
                        <Loader size="medium" type="primary" />
                    </LoaderContainer>
                ) : (
                    <StyledFullWidthButton
                        variant="primary"
                        onClick={handleReferClick}
                    >
                        Refer now
                    </StyledFullWidthButton>
                )}
            </ContactInputContainer>

            {showSuccessModal && (
                <ReferralSuccessModal onClose={handleCloseSuccessModal} referredPersonDetails={getReferredPersonDetails()} />
            )}
            {showFailureModal && (
                <ReferralFailureModal
                    onClose={handleCloseFailureModal}
                    errorMessage={errorMessage}
                    buttonText={shouldRetryOnError ? "Okay" : "Refer someone else"}
                />
            )}
        </>
    );
};

export default ReferralViewForm;
